/**
 * Payment result page (Square callback url).
 * @module components/Payment/Payment
 */

import React from 'react';
import { useLocation } from 'react-router-dom';

// Human readable messages for the error codes Square can return.
const ERROR_MESSAGES = {
  payment_canceled: 'Payment canceled',
  amount_invalid_format: 'The payment amount is invalid',
  amount_too_small: 'The payment amount is too small',
  amount_too_large: 'The payment amount is too large',
  client_not_supported: 'This version of Square is not supported',
  currency_code_missing: 'The currency code is missing',
  currency_not_supported: 'The currency is not supported',
  unsupported_currency_code: 'The currency is not supported',
  data_invalid: 'The payment data is invalid',
  invalid_request: 'The payment request is invalid',
  invalid_version: 'The Square API version is invalid',
  unsupported_api_version: 'The Square API version is not supported',
  invalid_client_id: 'The Square client id is invalid',
  no_network_connection: 'There is no network connection',
  not_logged_in: 'You are not logged in to Square',
  user_not_active: 'The Square user is not active',
  could_not_post: 'The payment could not be processed',
  payment_already_in_progress: 'A payment is already in progress',
  unexpected_error: 'An unexpected error occurred',
};

/**
 * Parse the `data` query parameter Square adds to the callback url.
 * @function parseData
 * @param {string} search Location search string.
 * @returns {Object|null} Parsed data or null.
 */
const parseData = (search) => {
  const raw = new URLSearchParams(search).get('data');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

/**
 * Payment component.
 * @function Payment
 * @returns {string} Markup of the component.
 */
const Payment = () => {
  const { search } = useLocation();
  const data = parseData(search);

  let title = 'Payment';
  let message = null;

  if (data?.status === 'ok') {
    title = 'Payment success';
  } else if (data?.status === 'error') {
    title = 'Payment failed';
    message =
      ERROR_MESSAGES[data.error_code] ||
      (data.error_code
        ? `Error: ${String(data.error_code).replaceAll('_', ' ')}`
        : 'Unknown error');
  } else {
    message = 'No payment information received.';
  }

  return (
    <div className="payment-result">
      <h1 className="content-title">{title}</h1>
      {message && <p>{message}</p>}
      {data?.status === 'ok' && data.transaction_id && (
        <p>Transaction id: {data.transaction_id}</p>
      )}
    </div>
  );
};

export default Payment;
